// src/pages/EditarPerfilCentral.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useNavigate } from 'react-router-dom';
import { uploadImagem } from '../services/uploadImagem';

import CropImagemQuadrada from '../components/CropImagemQuadrada';
import CropImagemCapa from '../components/CropImagemCapa';

import EditarPerfilTab from './EditarPerfilTab';
import EditarSiteTab from './EditarSiteTab';

export default function EditarPerfilCentral() {
  const { usuario, loading: authLoading, loadingUserDoc } = useAuth();
  const navigate = useNavigate();
  const hasFetchedRef = useRef(false);

  const [activeTab, setActiveTab] = useState('perfil'); // 'perfil' | 'site'

  const [dados, setDados] = useState({
    nome: '',
    telefone: '',
    creci: '',
    cpf: '',
    especialidade: '',
    descricao: '',
    cidade: '',
    estado: '',
    bairro: '',
    rua: '',
    numero: '',
    fotoPerfil: { url: '', path: '' }, // ✅ padronizado
  });

  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  // imagens
  const [preview, setPreview] = useState('');
  const [previewCapa, setPreviewCapa] = useState('');
  const [novaFotoFinal, setNovaFotoFinal] = useState(null);
  const [novaCapaFinal, setNovaCapaFinal] = useState(null);
  const [imagemCrop, setImagemCrop] = useState(null);
  const [imagemCropCapa, setImagemCropCapa] = useState(null);

  useEffect(() => {
    const fetchDados = async () => {
      if (!usuario || authLoading || loadingUserDoc || hasFetchedRef.current)
        return;
      hasFetchedRef.current = true;

      setLoading(true);
      setErro('');
      try {
        const docRef = doc(db, 'users', usuario.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();

          const fotoPerfilObj = data.fotoPerfil?.url
            ? data.fotoPerfil
            : { url: data.fotoPerfil || '', path: '' };

          setDados({
            nome: data.nome || '',
            telefone: data.telefone || '',
            creci: data.creci || '',
            cpf: data.cpf || '',
            especialidade: data.especialidade || '',
            descricao: data.descricao || '',
            cidade: data.endereco?.cidade || '',
            estado: data.endereco?.estado || '',
            bairro: data.endereco?.bairro || '',
            rua: data.endereco?.rua || '',
            numero: data.endereco?.numero || '',
            fotoPerfil: fotoPerfilObj,
          });

          setPreview(data.fotoPerfil?.url || data.fotoPerfil || '');
          setPreviewCapa(data.fotoCapa?.url || data.fotoCapa || '');
        } else {
          setErro('Dados do perfil não encontrados.');
        }
      } catch (err) {
        console.error('Erro ao buscar dados:', err);
        setErro('Erro ao carregar dados.');
      } finally {
        setLoading(false);
      }
    };

    fetchDados();
  }, [usuario, authLoading, loadingUserDoc]);

  const handleChange = (e) => {
    setDados((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const imageURL = URL.createObjectURL(file);
      setImagemCrop(imageURL);
    } else {
      setErro('A imagem deve ser JPG, PNG ou WebP.');
    }
    e.target.value = '';
  };

  const handleCapaChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const imageURL = URL.createObjectURL(file);
      setImagemCropCapa(imageURL);
    } else {
      setErro('A imagem da capa deve ser JPG, PNG ou WebP.');
    }
    e.target.value = '';
  };

  const handleSubmitPerfil = async (e) => {
    e.preventDefault();
    setErro('');
    setSucesso('');

    if (!usuario?.uid) {
      setErro('Usuário não autenticado.');
      return;
    }

    const camposObrigatorios = [
      'nome',
      'telefone',
      'creci',
      'cpf',
      'cidade',
      'estado',
      'bairro',
      'rua',
      'numero',
    ];

    for (const campo of camposObrigatorios) {
      if (!dados[campo]?.toString().trim()) {
        setErro(`Preencha o campo obrigatório: ${campo}`);
        return;
      }
    }

    setLoading(true);
    try {
      let fotoURL = dados.fotoPerfil; // ✅ pode ser objeto {url,path}
      let fotoCapaURL = previewCapa;

      if (novaFotoFinal) {
        fotoURL = await uploadImagem(novaFotoFinal, usuario, 'perfil.jpg');
      }

      if (novaCapaFinal) {
        fotoCapaURL = await uploadImagem(novaCapaFinal, usuario, 'capa.jpg');
      }

      const dadosParaSalvar = {
        nome: dados.nome,
        telefone: dados.telefone,
        creci: dados.creci,
        cpf: dados.cpf,
        especialidade: dados.especialidade,
        descricao: dados.descricao,

        fotoPerfil: fotoURL?.url ? fotoURL : { url: fotoURL || '', path: '' },
        fotoCapa: fotoCapaURL?.url
          ? fotoCapaURL
          : { url: fotoCapaURL || '', path: '' },

        endereco: {
          cidade: dados.cidade,
          estado: dados.estado,
          bairro: dados.bairro,
          rua: dados.rua,
          numero: dados.numero,
        },

        cadastroCompleto: true,
      };

      await setDoc(doc(db, 'users', usuario.uid), dadosParaSalvar, {
        merge: true,
      });

      setSucesso('Dados atualizados com sucesso!');
      setTimeout(() => navigate('/perfil'), 1500);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setErro('Erro ao salvar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-screen-lg mx-auto px-6 py-6 bg-white rounded-xl shadow-md">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Editar Perfil</h1>
          <p className="text-sm text-gray-500">
            Ajuste seus dados e configure seu site profissional público.
          </p>
        </div>
      </div>

      {erro && (
        <div className="mb-4 text-red-700 bg-red-100 border border-red-300 px-4 py-2 rounded">
          {erro}
        </div>
      )}
      {sucesso && (
        <div className="mb-4 text-green-700 bg-green-100 border border-green-300 px-4 py-2 rounded">
          {sucesso}
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6">
        <div className="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1">
          <button
            type="button"
            onClick={() => {
              setActiveTab('perfil');
              setErro('');
              setSucesso('');
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeTab === 'perfil'
                ? 'bg-white shadow text-gray-900'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Perfil
          </button>

          <button
            type="button"
            onClick={() => {
              setActiveTab('site');
              setErro('');
              setSucesso('');
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              activeTab === 'site'
                ? 'bg-white shadow text-gray-900'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Meu Site
          </button>
        </div>
      </div>

      {/* Conteúdo */}
      {activeTab === 'perfil' ? (
        <EditarPerfilTab
          dados={dados}
          loading={loading}
          preview={preview}
          previewCapa={previewCapa}
          onChange={handleChange}
          onSubmit={handleSubmitPerfil}
          onPickFoto={handleFileChange}
          onPickCapa={handleCapaChange}
        />
      ) : (
        <EditarSiteTab />
      )}

      {/* Crops */}
      {imagemCrop && (
        <CropImagemQuadrada
          imagem={imagemCrop}
          onCancel={() => setImagemCrop(null)}
          onCropComplete={(arquivoCortado) => {
            setNovaFotoFinal(arquivoCortado);
            setPreview(URL.createObjectURL(arquivoCortado));
            setImagemCrop(null);
          }}
        />
      )}

      {imagemCropCapa && (
        <CropImagemCapa
          imagem={imagemCropCapa}
          onCancel={() => setImagemCropCapa(null)}
          onCropComplete={(arquivoCortado) => {
            setNovaCapaFinal(arquivoCortado);
            setPreviewCapa(URL.createObjectURL(arquivoCortado));
            setImagemCropCapa(null);
          }}
        />
      )}
    </div>
  );
}
